import {
  Button,
  Divider,
  Icons,
  Panel,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import {
  isClusterManaged,
  isKentManagedCluster,
  useCluster,
} from '@cast/shared-cluster-context-data-access';
import { WorkloadOptimizationAgentStatus } from '@cast/types';
import {
  KarpenterLabelChip,
  useInstallKentScriptDialog,
} from '@cast/split-me/features/kent';
import { useAssignCredentialsDialogV2 } from '@cast/split-me/hooks/dialogs';
import { useWorkloadOptimizationAgentStatusQuery } from '@cast/split-me/hooks';

import { useAutoOptimization } from '../../_hooks/useAutoOptimization';
import { useCanUseOptimizationWizard } from '../../_hooks/useCanUseOptimizationWizard';
import { WizardScreenLayout } from '../wizard/WizardScreenLayout';
import { IncludedLabel } from './_components/IncludedLabel';
import { SelectableCard } from './_components/SelectableCard';

const FEATURE_OPTIONS = [
  {
    value: 'nodeAutoscaling' as const,
    title: 'Node autoscaling',
    description:
      'Adds and removes nodes automatically based on demand. Schedules periodic rebalancing to restructure your cluster layout.',
    icon: <Icons.CastCostOptimization size={32} variant="preset" />,
  },
  {
    value: 'workloadAutoscaling' as const,
    title: 'Workload autoscaling',
    description:
      'Right-sizes CPU and memory requests based on actual usage. Reduces over provisioning and eliminates wasted CPU and memory spend.',
    icon: <Icons.CastWorkloadAutoscaler size={32} variant="preset" />,
  },
];

const INCLUDED_FEATURES = [
  {
    title: 'Bin-packing',
    description:
      'Continuously bin-packs by safely evicting pods off underused nodes.',
    icon: <Icons.PaintBrushHousehold size={24} />,
  },
  {
    title: 'Scheduled rebalancing',
    description:
      'Restructures the whole node layout on a schedule for a cleaner, cheaper fit.',
    icon: <Icons.CastRebalancer size={24} />,
  },
];

export const FeaturesScreen = () => {
  const { state, onToggleFeature } = useAutoOptimization();
  const { canUseNodeAutoscaling } = useCanUseOptimizationWizard();
  const { cluster, isKentManaged } = useCluster();
  const { data: woopAgentStatus, isLoading: isWoopAgentStatusLoading } =
    useWorkloadOptimizationAgentStatusQuery({ clusterId: cluster.id });
  const { showInstallKentScript } = useInstallKentScriptDialog();
  const { openAssignCredentialsDialog } = useAssignCredentialsDialogV2();

  // Kent clusters ship Karpenter with the Kent agent, so node autoscaling is
  // always available there without extra components.
  const nodeAutoscalerInstalled = isKentManaged || isClusterManaged(cluster);
  const workloadAutoscalerInstalled =
    woopAgentStatus?.status === WorkloadOptimizationAgentStatus.RUNNING;
  const nodeAutoscalerMissing = !nodeAutoscalerInstalled;
  const workloadAutoscalerMissing =
    !isWoopAgentStatusLoading && !workloadAutoscalerInstalled;
  const hasMissingComponents =
    nodeAutoscalerMissing || workloadAutoscalerMissing;
  const missingFeatureNames = [
    ...(nodeAutoscalerMissing ? ['Node autoscaling'] : []),
    ...(workloadAutoscalerMissing ? ['Workload autoscaling'] : []),
  ];

  const openInstallComponentsDialog = () => {
    if (isKentManagedCluster(cluster)) {
      showInstallKentScript({ cluster });
    } else {
      openAssignCredentialsDialog({ cluster });
    }
  };

  return (
    <WizardScreenLayout
      title="Let's optimize your cluster."
      description="Select features to enable."
    >
      <Stack gap={0}>
        <Stack gap={8}>
          <Typography variant="h6">Select features to use</Typography>
          <Stack direction="row" gap={16} sx={{ alignItems: 'flex-start' }}>
            {FEATURE_OPTIONS.map((option) => (
              <SelectableCard
                key={option.value}
                value={option.value}
                selected={state.features[option.value]}
                onChange={onToggleFeature}
                title={option.title}
                titleSuffix={
                  option.value === 'nodeAutoscaling' && isKentManaged ? (
                    <KarpenterLabelChip />
                  ) : undefined
                }
                description={option.description}
                icon={option.icon}
                showCheckbox
                disabled={
                  option.value === 'nodeAutoscaling'
                    ? !canUseNodeAutoscaling || nodeAutoscalerMissing
                    : workloadAutoscalerMissing
                }
                testId={`feature-card-${option.value}`}
              />
            ))}
          </Stack>
          {hasMissingComponents ? (
            <Stack gap={8} sx={{ alignItems: 'flex-start' }}>
              <Typography
                variant="P12R"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                Install the missing components to enable{' '}
                {missingFeatureNames.join(' and ')}.
              </Typography>
              <Button
                variant="primary"
                size="md"
                onClick={openInstallComponentsDialog}
                testId="install-components-button"
              >
                Install components
              </Button>
            </Stack>
          ) : null}
        </Stack>
        <Divider sx={{ my: 24 }} />
        <Stack gap={8}>
          <IncludedLabel text="Feature included with above" />
          <Stack direction="row" gap={16} sx={{ alignItems: 'flex-start' }}>
            {INCLUDED_FEATURES.map((feature) => (
              <Panel
                key={feature.title}
                sx={{
                  width: '100%',
                  backgroundColor: tokens.theme.bg.structural.surfaceRaised,
                  border: `1px solid ${tokens.theme.border.structural.neutral}`,
                  p: 16,
                }}
              >
                <Stack gap={12}>
                  <Stack
                    sx={{
                      width: 40,
                      height: 40,
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      color: tokens.theme.fg.structural.secondary,
                    }}
                  >
                    {feature.icon}
                  </Stack>
                  <Stack gap={6}>
                    <Typography
                      variant="A1"
                      sx={{ color: tokens.theme.fg.structural.secondary }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="P12R"
                      sx={{ color: tokens.theme.fg.structural.secondary }}
                    >
                      {feature.description}
                    </Typography>
                  </Stack>
                </Stack>
              </Panel>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </WizardScreenLayout>
  );
};
